import type { NativeEvent, WebEvent } from "~/types/events";
import type { ScaleBoundUnit, ScaleData, ScaleModel } from "~/types/hardware";
import { defaultScaleDecimals } from "~/utils/hardware/scale-precision";

/**
 * A stand-in for the Elo wrapper and the CAS scale behind it, so the pairing
 * flow can be exercised in vitest and in a browser without hardware.
 *
 * It answers the web app's scale messages the way the wrapper does: a pairing
 * is stored, a connect resolves model, unit and decimals from the message or
 * the stored pairing, and readings stream from whatever sits on the platter.
 * A frame from the scale is the display digits without a decimal point, so
 * `placeItem("01150")` on a scale showing three decimals is 1.150 lb, and the
 * reading the web app gets depends only on the decimals the connection was
 * opened with, exactly as with the real driver. This is a simulation of that
 * contract, not the driver itself.
 */

export type FakeScaleOptions = {
  model: ScaleModel;
  /** What the physical scale is calibrated to. */
  unit: ScaleBoundUnit;
  device?: ScaleData;
  /** How often a frame arrives. */
  frameIntervalMs?: number;
  /** How long a PDN needs to hold a value before the wrapper calls it stable. */
  settleMs?: number;
};

type StoredPairing = {
  scale: ScaleData | null;
  model: ScaleModel | null;
  unit: ScaleBoundUnit | null;
  decimals: number | null;
};

type OpenConnection = {
  model: ScaleModel;
  unit: ScaleBoundUnit;
  decimals: number;
  timer: ReturnType<typeof setInterval>;
};

const KG_TO_G_SHIFT = 3;

/** The driver's rule: digits from the frame, point placed from the decimals. */
export const placeDecimalPoint = (
  digits: string,
  unit: ScaleBoundUnit,
  decimals: number
): { weight: number; unit: "g" | "lb" | "oz"; decimals: number } => {
  if (unit === "kg") {
    const gramDecimals = Math.max(0, decimals - KG_TO_G_SHIFT);
    const gramDigits =
      decimals < KG_TO_G_SHIFT
        ? digits + "0".repeat(KG_TO_G_SHIFT - decimals)
        : digits;
    return {
      weight: withDecimals(gramDigits, gramDecimals),
      unit: "g",
      decimals: gramDecimals
    };
  }
  return { weight: withDecimals(digits, decimals), unit, decimals };
};

const withDecimals = (digits: string, decimals: number): number => {
  if (decimals === 0) return parseInt(digits, 10);
  const padded = digits.padStart(decimals + 1, "0");
  const cut = padded.length - decimals;
  return Number(`${padded.slice(0, cut)}.${padded.slice(cut)}`);
};

export const createFakeScaleWrapper = ({
  model,
  unit,
  device = { deviceId: "1002", productId: "24577", vendorId: "1027" },
  frameIntervalMs = 100,
  settleMs = 500
}: FakeScaleOptions) => {
  const sent: WebEvent[] = [];
  let stored: StoredPairing = {
    scale: null,
    model: null,
    unit: null,
    decimals: null
  };
  let connection: OpenConnection | null = null;
  let platter: string | null = null;
  let heldMs = 0;
  let failNextConnect = false;
  let previousBridge: Window["ReactNativeWebView"];

  const emit = (event: NativeEvent) => {
    window.dispatchEvent(
      new MessageEvent("message", { data: JSON.stringify(event) })
    );
  };

  const frame = () => {
    if (!connection) return;
    heldMs += frameIntervalMs;
    const format = placeDecimalPoint("0", connection.unit, connection.decimals);

    if (platter === null) {
      emit({
        type: "SCALE_WEIGHT_UPDATE",
        payload: {
          status: "stable",
          weight: 0,
          unit: format.unit,
          decimals: format.decimals
        }
      });
      return;
    }

    const reading = placeDecimalPoint(
      platter,
      connection.unit,
      connection.decimals
    );
    // A PDN never reports instability; the wrapper infers it from a value
    // holding still. The PD-II says so itself and is taken at its word.
    const settled = connection.model === "pd2" || heldMs >= settleMs;
    emit({
      type: "SCALE_WEIGHT_UPDATE",
      payload: { status: settled ? "stable" : "unstable", ...reading }
    });
  };

  const stop = () => {
    if (!connection) return;
    clearInterval(connection.timer);
    connection = null;
  };

  const start = (open: Omit<OpenConnection, "timer">) => {
    stop();
    heldMs = 0;
    connection = { ...open, timer: setInterval(frame, frameIntervalMs) };
  };

  const handle = (event: WebEvent) => {
    switch (event.type) {
      case "DEVICE_DATA_GET_REQUEST":
        emit({
          type: "DEVICE_DATA_GET_SUCCESS",
          payload: {
            deviceId: "fake-elo",
            tillId: null,
            os: "android",
            osVersion: "fake",
            printer: null,
            scale: stored.scale,
            model: stored.model,
            scaleUnit: stored.unit,
            scaleDecimals: stored.decimals
          }
        });
        break;

      case "SCALE_DISCOVER_REQUEST":
        emit({ type: "SCALE_DISCOVER_SUCCESS", payload: [device] });
        break;

      case "SCALE_SELECT_REQUEST": {
        stop();
        const scale =
          "model" in event.payload.scale
            ? event.payload.scale.scale
            : event.payload.scale;
        stored = {
          scale,
          model: event.payload.model,
          unit: event.payload.unit ?? stored.unit ?? "g",
          // An explicit null clears; an older web build says nothing and the stored value stands
          decimals:
            "decimals" in event.payload
              ? event.payload.decimals
              : stored.decimals
        };
        emit({ type: "SCALE_SELECT_SUCCESS", payload: scale });
        break;
      }

      case "SCALE_REMOVE":
        stop();
        stored = { scale: null, model: null, unit: null, decimals: null };
        emit({ type: "SCALE_REMOVE_SUCCESS" });
        break;

      case "SCALE_CONNECT_REQUEST": {
        if (!event.payload) {
          emit({ type: "SCALE_CONNECT_ERROR", payload: "scale_not_found" });
          break;
        }
        if (failNextConnect) {
          failNextConnect = false;
          emit({
            type: "SCALE_CONNECT_ERROR",
            payload: "error_connecting_to_scale"
          });
          break;
        }
        const open = {
          model: event.payload.model ?? stored.model ?? "pd2",
          unit: event.payload.unit ?? stored.unit ?? "g"
        };
        const declared =
          "decimals" in event.payload
            ? event.payload.decimals ?? null
            : stored.decimals;
        const decimals =
          declared ?? defaultScaleDecimals(open.model, open.unit);

        if (
          connection &&
          connection.unit === open.unit &&
          connection.decimals === decimals
        ) {
          break;
        }
        start({ ...open, decimals });
        break;
      }

      case "SCALE_DISCONNECT_REQUEST":
        stop();
        break;

      default:
        break;
    }
  };

  return {
    /** Everything the web app sent, oldest first. */
    sent,
    /** The simulated scale as the wrapper would have stored it. */
    get stored() {
      return stored;
    },
    get isConnected() {
      return connection !== null;
    },
    attach() {
      previousBridge = window.ReactNativeWebView;
      window.ReactNativeWebView = {
        postMessage: (json: string) => {
          const event = JSON.parse(json) as WebEvent;
          sent.push(event);
          handle(event);
        }
      };
    },
    detach() {
      stop();
      window.ReactNativeWebView = previousBridge;
    },
    /** Puts an item on the platter; `displayDigits` is what the scale's own display shows, without the point. */
    placeItem(displayDigits: string) {
      platter = displayDigits.replace(/\D/g, "");
      heldMs = 0;
    },
    removeItem() {
      platter = null;
      heldMs = 0;
    },
    failNextConnect() {
      failNextConnect = true;
    },
    lastSent<T extends WebEvent["type"]>(type: T) {
      return [...sent]
        .reverse()
        .find(
          (event): event is Extract<WebEvent, { type: T }> =>
            event.type === type
        );
    },
    calibration: { model, unit }
  };
};

export type FakeScaleWrapper = ReturnType<typeof createFakeScaleWrapper>;
